const hasValue = (v) => v !== undefined && v !== null && v !== ''

// origin_op / origin_qi 可能是數字、數字字串或舊資料的 "q3"
const toId = (v) => {
  if (typeof v === 'number') return v
  const m = typeof v === 'string' ? v.match(/^q?(\d+)$/) : null
  return m ? Number(m[1]) : v
}
const sameId = (a, b) => toId(a) === toId(b)

/**
 * 帶入題（questionMappings 的目標題）答案重新對位。
 *
 * 帶入題選項的 value 是「來源題勾選子集合內的序位（0..n-1）」，來源題一改，
 * 同一個選項的序位就會變。這裡只用穩定身分把舊答案對到新序位 [9EZ-608]：
 * - 帶入的「其他／以上皆無」（slot.kind = 'other' | 'none'）：只認種類，
 *   不看 origin_op（舊資料的 origin_op 可能是漂移後的顯示位置，會和一般選項撞號），
 *   也不會配到一般選項
 * - 一般選項（沒有 kind）：只認 origin_op（origin_qi 兩邊都有時也要相符），
 *   只在一般選項之間比對，不用 title 或「新清單裡只有一個」當後備
 * - 舊資料一般選項沒有 origin_op：才用 title，且 title 在舊快照（prevSlots）與
 *   新清單的一般選項中都必須唯一（舊快照同名時分不出原選哪個，視為對不到）
 * - 續填標成身分不明的 slot（identityUnknown，見 useLoadProgress）：一律對不到，
 *   新清單裡身分不明的選項也不當一般選項候選
 * - 目標題自己的 'other'／'none' 字串值原樣保留
 * - 對不到 → 拿掉（單選回傳 undefined；複選只留還在的，全沒了回傳 undefined）
 *
 * @param prevValue  目標題目前的答案（單選為 value，複選為 value 陣列）
 * @param prevSlots  重建前的 choiceIndexMapRef.current[目標題]
 *                   （{ [value]: { index, title, origin_op, origin_qi, kind? } }）
 * @param newChoices 重建後的 choices（[{ value, text, origin_op, origin_qi, kind?, identityUnknown? }]）
 * @returns 新答案；undefined 代表應清空
 */
export function remapBringInAnswer(prevValue, prevSlots, newChoices) {
  // legacy title 後備：舊快照的一般選項中也只能有一個同名，否則分不出原選的是哪個
  const isUniquePrevTitle = (title) =>
    Object.values(prevSlots || {}).filter(
      (s) => s && typeof s === 'object' && !s.kind && s.title === title
    ).length === 1
  const remapOne = (v) => {
    // 目標題自己的「其他」/「以上皆無」不是帶入選項，原樣保留
    if (v === 'other' || v === 'none') return v
    const slot = prevSlots?.[v]
    if (!slot || typeof slot !== 'object') return undefined
    // 續填重建時取不到來源題、文字又像特殊選項：種類不明，不可當一般選項比 origin_op
    if (slot.identityUnknown) return undefined
    const sameSource = (c) =>
      !hasValue(slot.origin_qi) ||
      !hasValue(c.origin_qi) ||
      sameId(c.origin_qi, slot.origin_qi)
    let found
    if (slot.kind) {
      found = newChoices.filter((c) => c.kind === slot.kind && sameSource(c))
    } else {
      // 身分不明的 slot 可能是特殊選項，origin_op 會和一般選項撞號，不列入候選
      const plain = newChoices.filter(
        (c) => !c.kind && !c.identityUnknown && sameSource(c)
      )
      found = hasValue(slot.origin_op)
        ? plain.filter(
            (c) => hasValue(c.origin_op) && sameId(c.origin_op, slot.origin_op)
          )
        : hasValue(slot.title) && isUniquePrevTitle(slot.title)
        ? plain.filter((c) => c.text === slot.title)
        : []
    }
    return found.length === 1 ? found[0].value : undefined
  }

  if (Array.isArray(prevValue)) {
    const next = prevValue.map(remapOne).filter((v) => v !== undefined)
    return next.length > 0 ? next : undefined
  }
  if (!hasValue(prevValue)) return undefined
  return remapOne(prevValue)
}
